import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { apiCall } from 'modules/core/services/api/api'
import { TopicService } from 'openapi/services/TopicService'
import { analytics } from 'services/mixpanel/analytics-config'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useIdeaStore } from './idea-store'

// Shared step labels for error-tracking grouping. These show up as the `step` tag on
// every captured exception and as breadcrumb categories — keep them stable
// once they appear in production filters.
const TOPIC_FEATURE = 'topic_generation'
type TopicStep =
  | 'fetch'
  | 'pick'
  | 'dismiss'
  | 'generate_more_topics'
  | 'generate_more_posts'
  | 'dismiss_directed_batch'

function captureTopicError(err: unknown, step: TopicStep, extra?: Record<string, unknown>) {
  const error = err instanceof Error ? err : new Error(String(err))
  errorReportingService.captureException(error, {
    feature: TOPIC_FEATURE,
    step,
    ...extra,
  })
}

export type TopicStatus = 'suggested' | 'picked' | 'dismissed' | 'replaced'

export interface Topic {
  id: string
  headline: string
  status: TopicStatus
  batchId: string
  generatedAt: string
  position: number
  /** True while we've called pick() and are waiting for TopicCreativesReady. */
  isLoadingCreatives: boolean
}

export const TOPICS_PER_GENERATION = 3

interface TopicStore {
  topics: Topic[]
  selectedTopicId: string | null
  loading: boolean
  /**
   * Epoch ms of the last successful fetchTopics. Not persisted — used by the
   * Home page to refetch when the tab regains focus after the embedded signed
   * URLs (6h TTL) may have expired, so a long-open tab doesn't render dead media.
   */
  lastFetchedAt: number | null
  generatingMore: boolean
  /**
   * Number of new topics still expected from the in-flight surprise/direction
   * generation. Used to batch the topics-array update for the *non-directed*
   * (Surprise me) path so the switcher list grows once instead of three times.
   */
  pendingTopicCount: number
  /** Set of topicIds currently waiting on a "more posts" batch. */
  generatingMorePostsFor: Set<string>

  // ─── Directed-mode state ────────────────────────────────────────────────
  /** User-typed direction for the current directed-generation batch. */
  activeDirection: string | null
  /** batchId returned by /topics/generate for the active direction. */
  activeBatchId: string | null

  fetchTopics: () => Promise<void>
  pickTopic: (topicId: string) => Promise<void>
  dismissTopic: (topicId: string) => Promise<void>
  generateMoreTopics: (opts?: { userDirection?: string }) => Promise<void>
  generateMorePosts: (topicId: string) => Promise<void>
  /**
   * Clear directed mode and dismiss every still-suggested topic in the
   * active batch. Internal-only after May 2026: `generateMoreTopics` calls
   * this to tear down a stale directed batch before starting a new one.
   * No external UI surface invokes it (HomePage auto-picks the first
   * directed topic, so users never need a manual "clear" affordance).
   */
  clearActiveDirection: () => Promise<void>
  /** Pusher → topic:ready handler. */
  handleTopicReady: (data: { topicId: string; replacesTopicId?: string }) => void
  /** Pusher → topic:creatives:ready handler. */
  handleTopicCreativesReady: (data: { topicId: string }) => void
}

function sortByPosition(a: Topic, b: Topic): number {
  return a.position - b.position
}

function isVisible(t: Topic): boolean {
  return t.status === 'suggested' || t.status === 'picked'
}

// ─── Creatives-loading backstop ─────────────────────────────────────────────
// `isLoadingCreatives` / `generatingMorePostsFor` are normally cleared by the
// `TopicCreativesReady` Pusher event. That event fires exactly once per batch
// with NO fallback — if it's missed (fired before this tab subscribed, dropped
// on a flaky connection, or the generation loop died), the loading flags would
// spin forever (the bug this fixes). We arm a per-topic backstop whenever a
// topic enters a loading state and force-clear it after this deadline.
const CREATIVES_LOADING_BACKSTOP_MS = 90_000

// Topics whose creative generation we consider settled — the ready event
// arrived OR the backstop elapsed. `fetchTopics` consults this so it won't
// re-mark a settled topic as loading just because fewer than the expected
// number of Posts came back (a shape can fail before its Project shell exists,
// leaving the count permanently short). Cleared when a topic is re-picked.
const settledCreativeTopics = new Set<string>()
const creativesBackstops = new Map<string, ReturnType<typeof setTimeout>>()

function disarmCreativesBackstop(topicId: string): void {
  const timer = creativesBackstops.get(topicId)
  if (timer) {
    clearTimeout(timer)
    creativesBackstops.delete(topicId)
  }
}

function armCreativesBackstop(topicId: string): void {
  if (creativesBackstops.has(topicId)) return
  const timer = setTimeout(() => {
    creativesBackstops.delete(topicId)
    settledCreativeTopics.add(topicId)
    useTopicStore.setState(state => {
      const nextSet = new Set(state.generatingMorePostsFor)
      nextSet.delete(topicId)
      return {
        topics: state.topics.map(t => (t.id === topicId ? { ...t, isLoadingCreatives: false } : t)),
        generatingMorePostsFor: nextSet,
      }
    })
    // Pull whatever Posts actually landed so cards that DID generate render
    // (the failed ones surface their own Retry state via IdeaCard).
    useIdeaStore.getState().fetchIdeas()
  }, CREATIVES_LOADING_BACKSTOP_MS)
  creativesBackstops.set(topicId, timer)
}

export const useTopicStore = create<TopicStore>()(
  persist(
    (set, get) => ({
      topics: [],
      selectedTopicId: null,
      loading: true,
      lastFetchedAt: null,
      generatingMore: false,
      pendingTopicCount: 0,
      generatingMorePostsFor: new Set<string>(),
      activeDirection: null,
      activeBatchId: null,

      fetchTopics: async () => {
        try {
          const response = (await apiCall(TopicService.topicControllerListV1, null)) as {
            topics: Array<{
              id: string
              headline: string
              status: TopicStatus
              batchId: string
              generatedAt: string
              position: number
            }>
            postsByTopicId: Record<string, unknown[]>
          }
          const topics: Topic[] = response.topics
            .filter(t => t.status === 'suggested' || t.status === 'picked')
            .map(t => ({
              id: t.id,
              headline: t.headline,
              status: t.status,
              batchId: t.batchId,
              generatedAt: t.generatedAt,
              position: t.position,
              // A picked topic with fewer than the expected initial-batch count
              // means creative generation is still in flight — covers the
              // post-onboarding bootstrap (zero posts yet) AND the reload-mid-gen
              // case (1 or 2 posts arrived, third still being generated). Lets
              // the grid pre-allocate skeleton slots for the missing rows so the
              // user never sees an orphan card with a "More posts" button below
              // it. Cleared by handleTopicCreativesReady when the topic's gen
              // loop completes (regardless of per-shape success), or by the
              // backstop below if that event never arrives. `settledCreativeTopics`
              // stops a short post count (a shape that failed before its Project
              // shell existed) from re-marking the topic as loading on refetch.
              isLoadingCreatives:
                t.status === 'picked' &&
                !settledCreativeTopics.has(t.id) &&
                (response.postsByTopicId[t.id]?.length ?? 0) < TOPICS_PER_GENERATION,
            }))
            .sort(sortByPosition)

          // First picked-or-suggested topic is the default selection (matches
          // server bootstrap behavior: top topic auto-picked + creatives pre-genned).
          const currentSelected = get().selectedTopicId
          const stillVisible = currentSelected && topics.find(t => t.id === currentSelected)
          const selectedTopicId = stillVisible
            ? currentSelected
            : (topics.find(t => t.status === 'picked')?.id ?? topics[0]?.id ?? null)

          set({ topics, loading: false, selectedTopicId, lastFetchedAt: Date.now() })

          // Arm a backstop for any topic still mid-generation so a missed
          // `TopicCreativesReady` (e.g. fired before this tab loaded — the
          // reload-after-completion bug) can't leave skeletons spinning forever.
          for (const t of topics) {
            if (t.isLoadingCreatives) armCreativesBackstop(t.id)
          }

          // Hydrate the idea-store with the suggested Posts the controller
          // returned alongside topics, so the creative grid renders immediately
          // for whichever topic is selected.
          useIdeaStore.getState().hydrateFromTopics(response.postsByTopicId)
        } catch (err) {
          captureTopicError(err, 'fetch')
          console.error('Failed to fetch topics:', err)
          set({ loading: false })
        }
      },

      pickTopic: async (topicId: string) => {
        const topic = get().topics.find(t => t.id === topicId)
        if (!topic) return

        const ideasForTopic = useIdeaStore.getState().ideasForTopic(topicId)
        const hadCreatives = ideasForTopic.length >= 3

        // Picking a directed topic exits directed mode (other topics in the
        // batch stay around as regular suggestions for next time).
        const exitingDirected =
          get().activeBatchId !== null && topic.batchId === get().activeBatchId

        // Fresh generation cycle — clear any prior "settled" marker and arm a
        // backstop so a missed TopicCreativesReady self-heals the loading state.
        if (!hadCreatives) {
          settledCreativeTopics.delete(topicId)
          armCreativesBackstop(topicId)
        }

        set(state => ({
          selectedTopicId: topicId,
          topics: state.topics.map(t =>
            t.id === topicId ? { ...t, status: 'picked', isLoadingCreatives: !hadCreatives } : t
          ),
          ...(exitingDirected ? { activeDirection: null, activeBatchId: null } : {}),
        }))

        analytics.track(AnalyticsEvents.Home.TOPIC_PICKED, {
          topic_id: topicId,
          position: topic.position,
          had_creatives: hadCreatives,
        })

        errorReportingService.addBreadcrumb(`topic_picked: ${topicId}`, 'topic', {
          topicId,
          position: topic.position,
        })

        try {
          await apiCall(TopicService.topicControllerPickV1, null, topicId)
        } catch (err) {
          captureTopicError(err, 'pick', { topicId })
          console.error('Failed to pick topic:', err)
          disarmCreativesBackstop(topicId)
          set(state => ({
            topics: state.topics.map(t =>
              t.id === topicId ? { ...t, isLoadingCreatives: false } : t
            ),
          }))
        }
      },

      dismissTopic: async (topicId: string) => {
        const topic = get().topics.find(t => t.id === topicId)
        if (!topic) return

        analytics.track(AnalyticsEvents.Home.TOPIC_DISMISSED, {
          topic_id: topicId,
          position: topic.position,
        })

        // Optimistic remove. If this was the selected topic, fall back to next.
        const next = get().topics.filter(t => t.id !== topicId && isVisible(t))
        const wasSelected = get().selectedTopicId === topicId
        const newSelected = wasSelected ? (next[0]?.id ?? null) : get().selectedTopicId

        set({ topics: next, selectedTopicId: newSelected })

        if (wasSelected && newSelected) {
          // Auto-pick the next topic so the creative grid stays populated.
          void get().pickTopic(newSelected)
        }

        try {
          await apiCall(TopicService.topicControllerDismissV1, null, topicId)
        } catch (err) {
          captureTopicError(err, 'dismiss', { topicId })
          console.error('Failed to dismiss topic:', err)
          // Rollback — refetch ground truth.
          void get().fetchTopics()
        }
      },

      generateMoreTopics: async opts => {
        if (get().generatingMore) return

        const direction = opts?.userDirection?.trim()
        const isDirected = !!direction

        // Directed runs replace any existing directed batch. Tear down the
        // old one server-side first so we don't leave orphaned topics in the
        // suggestions list.
        if (isDirected && get().activeBatchId) {
          await get().clearActiveDirection()
        }

        set({
          generatingMore: true,
          // Non-directed (Surprise me) defers the fetch via pendingTopicCount
          // so the list grows once. Directed batches use pre-allocated skeleton
          // slots in the UI, so each event can fetch immediately and fill its
          // slot with no list resize.
          pendingTopicCount: isDirected ? 0 : TOPICS_PER_GENERATION,
          ...(isDirected ? { activeDirection: direction, activeBatchId: null } : {}),
        })

        analytics.track(AnalyticsEvents.Home.TOPIC_GENERATE_MORE, {
          mode: isDirected ? 'direction' : 'surprise',
          direction_length: direction?.length,
        })

        try {
          const response = (await apiCall(TopicService.topicControllerGenerateV1, null, {
            count: TOPICS_PER_GENERATION,
            userDirection: direction || undefined,
          })) as { status: string; batchId: string }

          if (isDirected) {
            set({ activeBatchId: response.batchId })
            // Race-safe: topic:ready events for this batch may have arrived
            // before activeBatchId was set. handleTopicReady refetched on each
            // arrival but couldn't clear generatingMore without knowing the
            // batchId. Now that we know it, re-check the count.
            const directedCount = get().topics.filter(t => t.batchId === response.batchId).length
            if (directedCount >= TOPICS_PER_GENERATION) {
              set({ generatingMore: false })
            }
          }
        } catch (err) {
          captureTopicError(err, 'generate_more_topics', {
            mode: isDirected ? 'direction' : 'surprise',
          })
          console.error('Failed to generate more topics:', err)
          set({
            generatingMore: false,
            ...(isDirected ? { activeDirection: null, activeBatchId: null } : {}),
          })
          return
        }

        // Backstop — if Pusher drops an event or the server is slow, force a
        // fetch + clear so the UI doesn't get stuck. Bumped to 15s because
        // directed batches can run 3 sequential LLM calls (~3s each).
        setTimeout(() => {
          if (!get().generatingMore) return
          set({ generatingMore: false, pendingTopicCount: 0 })
          void get().fetchTopics()
        }, 15000)
      },

      generateMorePosts: async (topicId: string) => {
        if (get().generatingMorePostsFor.has(topicId)) return
        set(state => {
          const next = new Set(state.generatingMorePostsFor)
          next.add(topicId)
          return { generatingMorePostsFor: next }
        })
        // Backstop the "more posts" spinner the same way as initial gen, in
        // case the final TopicCreativesReady never lands.
        armCreativesBackstop(topicId)

        try {
          await apiCall(TopicService.topicControllerGenerateMoreV1, null, topicId)
          // New posts land via PostReady (per-card) and a final
          // TopicCreativesReady (which clears this flag in handleTopicCreativesReady).
        } catch (err) {
          captureTopicError(err, 'generate_more_posts', { topicId })
          console.error('Failed to generate more posts:', err)
          disarmCreativesBackstop(topicId)
          set(state => {
            const next = new Set(state.generatingMorePostsFor)
            next.delete(topicId)
            return { generatingMorePostsFor: next }
          })
        }
      },

      clearActiveDirection: async () => {
        const batchId = get().activeBatchId
        if (!batchId) {
          set({ activeDirection: null, activeBatchId: null, generatingMore: false })
          return
        }

        // Optimistically remove all topics in the batch from the local store
        // so the switcher returns to default mode immediately. Also clear
        // generatingMore — late-arriving Pusher events for this batch will
        // fall through to the default-mode path and refetch harmlessly
        // (the topics are now status='dismissed' server-side and won't
        // re-appear in fetchTopics).
        set(state => ({
          topics: state.topics.filter(t => t.batchId !== batchId),
          activeDirection: null,
          activeBatchId: null,
          generatingMore: false,
        }))

        try {
          await apiCall(TopicService.topicControllerDismissBatchV1, null, batchId)
        } catch (err) {
          captureTopicError(err, 'dismiss_directed_batch', { batchId })
          console.error('Failed to dismiss directed batch:', err)
          // Reconcile with server truth on failure.
          void get().fetchTopics()
        }
      },

      handleTopicReady: data => {
        // Use activeDirection (set synchronously when generation starts) as the
        // "directed flow in progress" gate, NOT activeBatchId — the batchId
        // is only known after the POST returns and a topic:ready event can
        // race the response. If we used activeBatchId here, an early event
        // would fall through to the default-mode path and prematurely clear
        // generatingMore, collapsing the slot reservation.
        if (get().activeDirection !== null) {
          void get()
            .fetchTopics()
            .then(() => {
              const batchId = get().activeBatchId
              // batchId still unknown — POST is in flight. Don't touch
              // generatingMore here; generateMoreTopics rechecks once the
              // batchId lands.
              if (!batchId) return
              const directedCount = get().topics.filter(t => t.batchId === batchId).length
              if (directedCount >= TOPICS_PER_GENERATION) {
                set({ generatingMore: false })
              }
            })
          return
        }

        // Default-mode (Surprise me) batched path: defer fetch + clear until
        // the whole batch has landed, so the switcher list updates once with
        // all N new rows instead of growing N times. Bootstrap, single
        // replacement, and any other one-off topic (pendingTopicCount === 0)
        // still fetch immediately.
        const pending = get().pendingTopicCount
        if (pending > 0) {
          const remaining = pending - 1
          if (remaining === 0) {
            set({ generatingMore: false, pendingTopicCount: 0 })
            void get().fetchTopics()
          } else {
            set({ pendingTopicCount: remaining })
          }
        } else {
          void get().fetchTopics()
          set({ generatingMore: false })
        }
        void data
      },

      handleTopicCreativesReady: data => {
        // Event arrived — generation is settled; drop the backstop so it can't
        // fire a redundant force-clear, and remember it so a later refetch with
        // a short post count doesn't re-mark the topic as loading.
        settledCreativeTopics.add(data.topicId)
        disarmCreativesBackstop(data.topicId)
        set(state => {
          const nextSet = new Set(state.generatingMorePostsFor)
          nextSet.delete(data.topicId)
          return {
            topics: state.topics.map(t =>
              t.id === data.topicId ? { ...t, isLoadingCreatives: false } : t
            ),
            generatingMorePostsFor: nextSet,
          }
        })
        // Idea-store refetches the suggested Posts so the grid swaps from
        // skeletons to live cards.
        useIdeaStore.getState().fetchIdeas()
      },
    }),
    {
      name: 'taka-topic-store',
      // Persist only the user's pick — everything else is server-derived
      // and would leak between sessions if cached. The fetchTopics handler
      // already validates the persisted ID against the live topic list and
      // falls back gracefully if the topic no longer exists.
      partialize: (state): { selectedTopicId: string | null } => ({
        selectedTopicId: state.selectedTopicId,
      }),
    }
  )
)
